<script setup lang="ts">
import { computed, ref } from 'vue';
import { NxtCheckbox, NxtIcon } from '@future-harry/nxt-styles';
import { FilterOption } from '@/modules/content-blocks/interfaces/jobs-block.interface';

const isOpen = ref(false);
const viewport = inject('viewport');

const props = defineProps<{
   title: string;
   options: FilterOption[];
   useObjectFormat?: boolean;
   useCityFormat?: boolean;
}>();

const selectedValues = defineModel<number[] | { value: number }[] | { city_id: number; radius: string }[]>({ default: [] });

const isSelected = computed(() => (value: number) => {
   if (!selectedValues.value) return false;

   if (props.useObjectFormat) {
      return (selectedValues.value as { value: number }[]).some((item) => item.value === value);
   }

   if (props.useCityFormat) {
      return (selectedValues.value as any[]).some((item) => item.city_id === value);
   }

   return (selectedValues.value as number[]).includes(value);
});

const toggleOption = (value: number) => {
   if (props.useObjectFormat) {
      const arr = selectedValues.value as { value: number }[];
      const index = arr.findIndex((item) => item.value === value);

      if (index > -1) {
         selectedValues.value = arr.filter((_, i) => i !== index);
      } else {
         selectedValues.value = [...arr, { value }];
      }
   } else if (props.useCityFormat) {
      const arr = selectedValues.value as any[];
      const index = arr.findIndex((item) => item.city_id === value);

      if (index > -1) {
         selectedValues.value = arr.filter((_, i) => i !== index);
      } else {
         selectedValues.value = [...arr, { city_id: value, radius: '5' }];
      }
   } else {
      const arr = selectedValues.value as number[];
      const index = arr.indexOf(value);

      if (index > -1) {
         selectedValues.value = arr.filter((_, i) => i !== index);
      } else {
         selectedValues.value = [...arr, value];
      }
   }
};
</script>

<template>
   <div class="filter-group" :class="{ 'is-open': isOpen, 'is-mobile': viewport === 'mobile' }">
      <h4 @click="isOpen = !isOpen">
         {{ title }}
         <NxtIcon :name="isOpen ? 'chevron-down' : 'chevron-right'" size="1rem" />
      </h4>
      <div class="filter-group__options">
         <div v-if="options.length === 0">{{ $tb('public.positions.no-results') }}</div>
         <NxtCheckbox
            v-for="option in options"
            :key="option.value"
            :label="`${option.label} (${option.count})`"
            :name="option.label"
            :model-value="isSelected(option.value)"
            @update:model-value="toggleOption(option.value)"
         />
      </div>
   </div>
</template>

<style scoped lang="scss">
.filter-group {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);
   margin-bottom: var(--nxt-gutter-large);

   h4 {
      margin: 0;
      font-size: var(--nxt-font-small);
      font-weight: var(--nxt-font-bold);

      .nxt-icon {
         display: none;
      }
   }

   &__options {
      display: flex;
      flex-direction: column;
      gap: var(--nxt-gutter);
   }

   @mixin mobile-styles {
      gap: 0;

      h4 {
         display: flex;
         align-items: center;
         justify-content: space-between;
         cursor: pointer;
         padding-bottom: var(--nxt-gutter-small);

         .nxt-icon {
            display: inline;
         }
      }

      .filter-group__options {
         display: none;
      }

      &.is-open {
         .filter-group__options {
            display: flex;
            padding-top: var(--nxt-gutter);
         }
      }
   }

   @media (max-width: 400px) {
      @include mobile-styles;
   }

   &.is-mobile {
      @include mobile-styles;
   }
}
</style>
